import { useState, useEffect } from 'preact/hooks'
import { useForm } from 'react-hook-form'
import { X, CloudUpload, ArrowRight, Upload, AlertTriangle, Info } from 'lucide-preact'
import type { WebAssetViewDto } from '@/api/asset'
import { TextBoxField, FileUpload, FormActions } from '@/components'
import { useToast } from '@/hooks/useToast'
import { BRAND_CLASSES } from '@/constants/theme'
import { ASSET_TYPE_META, DEFAULT_ASSET_META } from '../constants/assetMeta'
import { buildAcceptString } from '../constants/fileRestrictions'
import { effectiveSettingsState } from '@/state/workflowSettings'
import { GoogleDrivePicker } from '@/components/common/GoogleDrivePicker'
import { OneDrivePicker } from '@/components/common/OneDrivePicker'
import { DropboxChooser } from '@/components/common/DropboxChooser'
import { GoogleDriveIcon } from '@/components/common/icons/GoogleDriveIcon'
import { OneDriveIcon } from '@/components/common/icons/OneDriveIcon'
import { DropboxIcon } from '@/components/common/icons/DropboxIcon'
import type { GoogleDriveFile } from '@/components/common/GoogleDrivePicker'
import type { OneDriveFile } from '@/components/common/OneDrivePicker'
import type { DropboxFile } from '@/components/common/DropboxChooser'
import { useGoogleDriveAuth } from '@/hooks/useGoogleDriveAuth'
import { useOneDriveAuth } from '@/hooks/useOneDriveAuth'
import { useDropboxAuth } from '@/hooks/useDropboxAuth'

type UploadMethod = 'file' | 'googledrive' | 'onedrive' | 'dropbox'

const DIRECT_UPLOAD_MAX_MB = 100
const LARGE_FILE_WARNING_MB = 50

interface UploadVersionModalProps {
  isOpen: boolean
  onClose: () => void
  assetDetail: WebAssetViewDto
  onUploadVersion: (file: File, versionComment?: string) => Promise<void>
  onUploadVersionByCloudStorage: (fileId: string, fileName: string, mimeType: string, provider: string, versionComment?: string) => Promise<void>
}

interface VersionFormData {
  VersionComment: string
}

export function UploadVersionModal({ isOpen, onClose, assetDetail, onUploadVersion, onUploadVersionByCloudStorage }: UploadVersionModalProps) {
  const { showError } = useToast()
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>('file')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | undefined>()
  const [pickedDriveFile, setPickedDriveFile] = useState<GoogleDriveFile | null>(null)
  const [driveError, setDriveError] = useState<string | undefined>()
  const [pickedOneDriveFile, setPickedOneDriveFile] = useState<OneDriveFile | null>(null)
  const [oneDriveError, setOneDriveError] = useState<string | undefined>()
  const [pickedDropboxFile, setPickedDropboxFile] = useState<DropboxFile | null>(null)
  const [dropboxError, setDropboxError] = useState<string | undefined>()
  const googleDriveAuth = useGoogleDriveAuth(isOpen)
  const oneDriveAuth = useOneDriveAuth(isOpen)
  const dropboxAuth = useDropboxAuth(isOpen)

  // Read effective workflow settings from signal (loaded by WebAssetsTab)
  const { settings: effectiveSettings, loading: loadingSettings } = effectiveSettingsState.value
  const fileRestrictions = effectiveSettings?.FileRestrictions ?? null

  const {
    control,
    handleSubmit,
    reset,
  } = useForm<VersionFormData>({
    mode: 'onChange',
    defaultValues: { VersionComment: '' },
  })

  const currentVersion = assetDetail.CurrentVersionNumber
  const nextVersion = currentVersion + 1

  const typeMeta = ASSET_TYPE_META[assetDetail.Type] || DEFAULT_ASSET_META
  const TypeIcon = typeMeta.Icon

  // ESC key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) handleClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, isSubmitting])

  const acceptString = fileRestrictions ? buildAcceptString(fileRestrictions) : null
  const settingsMaxMB = fileRestrictions
    ? Math.round(fileRestrictions.MaxFileSizeBytes / 1048576)
    : 500
  // Cap direct upload at 100MB regardless of settings
  const maxSizeMB = Math.min(settingsMaxMB, DIRECT_UPLOAD_MAX_MB)
  const hasSettings = fileRestrictions !== null
  const hasAllowedFileTypes = acceptString !== null

  // File size warnings
  const fileSizeMB = selectedFile ? selectedFile.size / 1048576 : 0
  const isFileOverLimit = selectedFile !== null && fileSizeMB > DIRECT_UPLOAD_MAX_MB
  const isFileLarge = selectedFile !== null && fileSizeMB > LARGE_FILE_WARNING_MB && !isFileOverLimit

  const onSubmit = async (data: VersionFormData) => {
    if (uploadMethod === 'file') {
      if (!selectedFile) {
        setFileError('Please select a file to upload')
        return
      }
      if (isFileOverLimit) return

      setIsSubmitting(true)
      try {
        await onUploadVersion(selectedFile, data.VersionComment.trim() || undefined)
        handleClose()
      } catch {
        showError('Upload failed', 'An unexpected error occurred while uploading')
      } finally {
        setIsSubmitting(false)
      }
    } else if (uploadMethod === 'googledrive') {
      if (!pickedDriveFile) return

      setIsSubmitting(true)
      try {
        await onUploadVersionByCloudStorage(
          pickedDriveFile.fileId,
          pickedDriveFile.fileName,
          pickedDriveFile.mimeType,
          'GoogleDrive',
          data.VersionComment.trim() || undefined
        )
        handleClose()
      } catch {
        showError('Upload failed', 'An unexpected error occurred while uploading')
      } finally {
        setIsSubmitting(false)
      }
    } else if (uploadMethod === 'onedrive') {
      if (!pickedOneDriveFile) return

      setIsSubmitting(true)
      try {
        await onUploadVersionByCloudStorage(
          pickedOneDriveFile.fileId,
          pickedOneDriveFile.fileName,
          pickedOneDriveFile.mimeType,
          'OneDrive',
          data.VersionComment.trim() || undefined
        )
        handleClose()
      } catch {
        showError('Upload failed', 'An unexpected error occurred while uploading')
      } finally {
        setIsSubmitting(false)
      }
    } else if (uploadMethod === 'dropbox') {
      if (!pickedDropboxFile) return

      setIsSubmitting(true)
      try {
        // Version-upload API still expects the Dropbox direct link in CloudStorageFileId;
        // it has no DirectDownloadUrl field (create-only, per ORB-320 / orbitalx-api#199).
        await onUploadVersionByCloudStorage(
          pickedDropboxFile.downloadLink,
          pickedDropboxFile.fileName,
          pickedDropboxFile.mimeType,
          'Dropbox',
          data.VersionComment.trim() || undefined
        )
        handleClose()
      } catch {
        showError('Upload failed', 'An unexpected error occurred while uploading')
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleClose = () => {
    reset({ VersionComment: '' })
    setSelectedFile(null)
    setFileError(undefined)
    setPickedDriveFile(null)
    setDriveError(undefined)
    setPickedOneDriveFile(null)
    setOneDriveError(undefined)
    setPickedDropboxFile(null)
    setDropboxError(undefined)
    setIsSubmitting(false)
    setUploadMethod('file')
    onClose()
  }

  const isFileModeSaveDisabled = uploadMethod === 'file' && (!selectedFile || isFileOverLimit || !hasSettings || !hasAllowedFileTypes)
  const isGoogleDriveModeSaveDisabled = uploadMethod === 'googledrive' && !pickedDriveFile
  const isOneDriveModeSaveDisabled = uploadMethod === 'onedrive' && !pickedOneDriveFile
  const isDropboxModeSaveDisabled = uploadMethod === 'dropbox' && !pickedDropboxFile

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-version-modal-title"
        className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${BRAND_CLASSES.BG.PRIMARY_LIGHT}`}>
              <CloudUpload className={`w-5 h-5 ${BRAND_CLASSES.TEXT.PRIMARY}`} />
            </div>
            <h2 id="upload-version-modal-title" className="text-xl font-semibold text-slate-900 dark:text-white">
              Upload New Version
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close modal"
            className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {/* Asset Info Card */}
            <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600">
              {/* Type Icon */}
              <div className={`flex-shrink-0 p-2 rounded-lg ${typeMeta.badgeBg}`}>
                <TypeIcon className={`w-5 h-5 ${typeMeta.color}`} />
              </div>

              {/* Name + Type */}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {assetDetail.Name}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {assetDetail.Type}
                </p>
              </div>

              {/* Version Transition */}
              <div className="flex-shrink-0 flex items-center gap-1.5 text-sm">
                <span className="px-2 py-0.5 rounded bg-slate-200 dark:bg-slate-600 text-slate-600 dark:text-slate-300 font-medium">
                  v{currentVersion}
                </span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                <span className="px-2 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium">
                  v{nextVersion}
                </span>
              </div>
            </div>

            {/* Upload Method Toggle */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setUploadMethod('file'); setPickedDriveFile(null); setDriveError(undefined); setPickedOneDriveFile(null); setOneDriveError(undefined); setPickedDropboxFile(null); setDropboxError(undefined) }}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  uploadMethod === 'file'
                    ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
              >
                <Upload className="w-4 h-4" />
                File Upload
              </button>
              <button
                type="button"
                onClick={() => { setUploadMethod('googledrive'); setSelectedFile(null); setFileError(undefined); setPickedOneDriveFile(null); setOneDriveError(undefined); setPickedDropboxFile(null); setDropboxError(undefined) }}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  uploadMethod === 'googledrive'
                    ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
              >
                <GoogleDriveIcon className="w-4 h-4" />
                Google Drive
              </button>
              <button
                type="button"
                onClick={() => { setUploadMethod('onedrive'); setSelectedFile(null); setFileError(undefined); setPickedDriveFile(null); setDriveError(undefined); setPickedDropboxFile(null); setDropboxError(undefined) }}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  uploadMethod === 'onedrive'
                    ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
              >
                <OneDriveIcon className="w-4 h-4" />
                OneDrive
              </button>
              <button
                type="button"
                onClick={() => { setUploadMethod('dropbox'); setSelectedFile(null); setFileError(undefined); setPickedDriveFile(null); setDriveError(undefined); setPickedOneDriveFile(null); setOneDriveError(undefined) }}
                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  uploadMethod === 'dropbox'
                    ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 shadow-sm'
                    : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
              >
                <DropboxIcon className="w-4 h-4" />
                Dropbox
              </button>
            </div>

            {/* File Upload Mode */}
            {uploadMethod === 'file' && (
              <>
                {loadingSettings ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-4">
                    <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                    Loading file restrictions...
                  </div>
                ) : !hasSettings ? (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Workflow settings must be configured before uploading.
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Go to Settings &gt; Asset Workflow to configure file restrictions.
                    </p>
                  </div>
                ) : !hasAllowedFileTypes ? (
                  <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      No file types are currently allowed.
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Enable at least one file category or configure allowed extensions in workflow settings.
                    </p>
                  </div>
                ) : (
                  <>
                    <FileUpload
                      label="File"
                      required
                      selectedFile={selectedFile}
                      onFileChange={(file) => {
                        setSelectedFile(file)
                        if (file) setFileError(undefined)
                      }}
                      onError={setFileError}
                      error={fileError}
                      accept={acceptString}
                      maxSizeMB={maxSizeMB}
                      helperText={`Select the file for this version (max ${maxSizeMB} MB)`}
                    />

                    {/* 100MB hard block */}
                    {isFileOverLimit && (
                      <div className="flex items-start gap-2.5 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                        <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700 dark:text-red-300">
                          Files over {DIRECT_UPLOAD_MAX_MB} MB cannot be uploaded directly. Use a cloud storage provider instead.
                        </p>
                      </div>
                    )}

                    {/* 50MB soft warning */}
                    {isFileLarge && (
                      <div className="flex items-start gap-2.5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-3">
                        <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-700 dark:text-blue-300">
                          Large file detected. For faster uploads, consider using a cloud storage provider for files over {LARGE_FILE_WARNING_MB} MB.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Google Drive Mode */}
            {uploadMethod === 'googledrive' && (
              <>
                <GoogleDrivePicker
                  auth={googleDriveAuth}
                  onFilePicked={(file) => {
                    setPickedDriveFile(file)
                    setDriveError(undefined)
                  }}
                  onError={setDriveError}
                />
                {driveError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{driveError}</p>
                )}
              </>
            )}

            {/* OneDrive Mode */}
            {uploadMethod === 'onedrive' && (
              <>
                <OneDrivePicker
                  auth={oneDriveAuth}
                  onFilePicked={(file) => {
                    setPickedOneDriveFile(file)
                    setOneDriveError(undefined)
                  }}
                  onError={setOneDriveError}
                />
                {oneDriveError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{oneDriveError}</p>
                )}
              </>
            )}

            {/* Dropbox Mode */}
            {uploadMethod === 'dropbox' && (
              <>
                <DropboxChooser
                  auth={dropboxAuth}
                  onFilePicked={(file) => {
                    setPickedDropboxFile(file)
                    setDropboxError(undefined)
                  }}
                  onError={setDropboxError}
                />
                {dropboxError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{dropboxError}</p>
                )}
              </>
            )}

            {/* Release Notes */}
            <TextBoxField
              control={control}
              name="VersionComment"
              label="Release Notes"
              multiline
              rows={3}
              placeholder="Describe what changed in this version..."
              rules={{
                validate: (v) => !v || v.trim().length > 0 || 'Version comment cannot be empty',
                maxLength: { value: 2000, message: 'Version comment cannot exceed 2000 characters' },
              }}
            />
          </div>

          {/* Footer */}
          <FormActions
            isSubmitting={isSubmitting}
            isSaveDisabled={isSubmitting || isFileModeSaveDisabled || isGoogleDriveModeSaveDisabled || isOneDriveModeSaveDisabled || isDropboxModeSaveDisabled}
            onCancel={handleClose}
            saveText={isSubmitting ? 'Uploading...' : `Upload v${nextVersion}`}
          />
        </form>
      </div>
    </div>
  )
}
